import React from 'react';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema, softwareAppSchema, faqSchema } from '../utils/structuredData';
import { ArrowRightIcon, CheckCircle2Icon, SearchIcon, TrendingUpIcon, ZapIcon, SparklesIcon, ExternalLinkIcon, StarIcon, ImageIcon, LinkIcon, CodeIcon, GaugeIcon, MapIcon, ShieldCheckIcon, RocketIcon, GlobeIcon } from 'lucide-react';
const SHOPIFY_URL = 'https://apps.shopify.com/seo-suite';
function InstallButton({
  size = 'md'
}: {
  size?: 'md' | 'lg';
}) {
  const pad = size === 'lg' ? 'px-10 py-5 text-lg' : 'px-8 py-4 text-base';
  return <a href={SHOPIFY_URL} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center justify-center gap-2 bg-emerald-500 text-white ${pad} rounded-full font-bold hover:bg-emerald-400 transition-all duration-300 hover:scale-105 shadow-[0_0_30px_rgba(16,185,129,0.3)]`} data-id="element-1244">
      Install on Shopify <ExternalLinkIcon className="w-5 h-5" data-id="element-1245" />
    </a>;
}
function StarRow() {
  return <div className="flex items-center gap-1" data-id="element-1246">
      {Array.from({
      length: 5
    }).map((_, i) => <StarIcon key={i} className="w-4 h-4 text-emerald-400 fill-emerald-400" data-id="element-1247" />)}
    </div>;
}
// Reusable "browser/app" frame for feature screenshots
function AppFrame({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="glass-card border-white/10 p-2 rounded-2xl relative shadow-2xl shadow-black/50" data-id="element-1248">
      <div className="bg-[#0A0A0A] rounded-xl overflow-hidden border border-white/5" data-id="element-1249">
        <div className="h-10 border-b border-white/10 flex items-center px-4 gap-2 bg-white/5" data-id="element-1250">
          <div className="w-3 h-3 rounded-full bg-red-500/50" data-id="element-1251" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/50" data-id="element-1252" />
          <div className="w-3 h-3 rounded-full bg-green-500/50" data-id="element-1253" />
        </div>
        <div className="p-6" data-id="element-1254">{children}</div>
      </div>
    </div>;
}
interface FeatureBlock {
  eyebrow: string;
  title: string;
  desc: string;
  bullets: string[];
  mockup: React.ReactNode;
}
const FEATURE_BLOCKS: FeatureBlock[] = [{
  eyebrow: 'AI Meta Optimization',
  title: 'Rank higher with AI-written meta tags',
  desc: 'SEO Suite analyzes every product, collection, and blog post and writes keyword-rich meta titles and descriptions that Google loves and shoppers click, in bulk, in seconds.',
  bullets: ['AI-generated titles & descriptions for your entire catalog', 'Bulk-apply across thousands of pages in one click', 'Live Google preview so you see results before publishing'],
  mockup: <div className="space-y-4" data-id="element-1255">
        <div className="flex items-center justify-between" data-id="element-1256">
          <span className="text-sm font-semibold text-white" data-id="element-1257">
            Meta Tag Generator
          </span>
          <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-full border border-emerald-400/20" data-id="element-1258">
            AI Ready
          </span>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/5 p-4" data-id="element-1259">
          <p className="text-blue-400 text-sm font-medium mb-1 truncate" data-id="element-1260">
            Organic Cotton T-Shirt — Soft &amp; Sustainable | Your Store
          </p>
          <p className="text-emerald-400/70 text-xs mb-2" data-id="element-1261">
            yourstore.com › products › organic-cotton-tee
          </p>
          <p className="text-gray-400 text-xs leading-relaxed" data-id="element-1262">
            Shop our breathable organic cotton t-shirts. Ethically made,
            ultra-soft, and built to last. Free shipping on orders over $50.
          </p>
        </div>
        <div className="flex items-center justify-between text-xs" data-id="element-1263">
          <span className="text-gray-500" data-id="element-1264">SEO score</span>
          <span className="text-emerald-400 font-bold" data-id="element-1265">Excellent · 96</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden" data-id="element-1266">
          <div className="h-full w-[96%] bg-emerald-500 rounded-full" data-id="element-1267" />
        </div>
      </div>
}, {
  eyebrow: 'Image SEO',
  title: 'Faster pages, better image rankings',
  desc: 'Heavy images kill your speed score and your rankings. SEO Suite compresses every image without quality loss and auto-writes descriptive ALT text so you show up in Google Images too.',
  bullets: ['Lossless bulk image compression for the whole store', 'Automatic descriptive ALT tags for accessibility & SEO', 'Lazy-loading and next-gen formats for faster load times'],
  mockup: <div className="space-y-3" data-id="element-1268">
        <div className="flex items-center justify-between mb-1" data-id="element-1269">
          <span className="text-sm font-semibold text-white" data-id="element-1270">
            Image Optimizer
          </span>
          <span className="text-xs text-white font-bold" data-id="element-1271">12,450 optimized</span>
        </div>
        {[{
      name: 'hero-banner.jpg',
      before: '2.4 MB',
      after: '310 KB'
    }, {
      name: 'product-01.png',
      before: '1.8 MB',
      after: '240 KB'
    }, {
      name: 'lifestyle-shot.jpg',
      before: '3.1 MB',
      after: '410 KB'
    }].map(img => <div key={img.name} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 p-3" data-id="element-1272">
            <div className="w-9 h-9 rounded-md bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0" data-id="element-1273">
              <ImageIcon className="w-4 h-4 text-emerald-400" data-id="element-1274" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-1275">
              <p className="text-xs text-gray-300 truncate" data-id="element-1276">{img.name}</p>
              <p className="text-[11px] text-gray-500" data-id="element-1277">
                <span className="line-through" data-id="element-1278">{img.before}</span> →{' '}
                <span className="text-emerald-400" data-id="element-1279">{img.after}</span>
              </p>
            </div>
            <CheckCircle2Icon className="w-4 h-4 text-emerald-400 shrink-0" data-id="element-1280" />
          </div>)}
      </div>
}, {
  eyebrow: 'Rich Results',
  title: 'Win rich snippets with structured data',
  desc: 'SEO Suite injects valid JSON-LD structured data so Google can show your star ratings, prices, and stock status right in the search results, driving dramatically higher click-through rates.',
  bullets: ['Automatic Product, Breadcrumb & Organization schema', 'Star ratings & pricing eligible for rich snippets', 'Validated against Google’s structured-data rules'],
  mockup: <div className="space-y-4" data-id="element-1281">
        <span className="text-sm font-semibold text-white" data-id="element-1282">
          Google Rich Result Preview
        </span>
        <div className="rounded-lg border border-white/10 bg-white/5 p-4" data-id="element-1283">
          <p className="text-blue-400 text-sm font-medium mb-1" data-id="element-1284">
            Premium Leather Wallet | Your Store
          </p>
          <div className="flex items-center gap-2 mb-2" data-id="element-1285">
            <StarRow data-id="element-1286" />
            <span className="text-xs text-gray-400" data-id="element-1287">4.9 (612) · In stock</span>
          </div>
          <p className="text-emerald-400 text-sm font-bold mb-1" data-id="element-1288">$89.00</p>
          <p className="text-gray-400 text-xs leading-relaxed" data-id="element-1289">
            Handcrafted full-grain leather wallet with RFID protection.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-emerald-400" data-id="element-1290">
          <CodeIcon className="w-4 h-4" data-id="element-1291" /> JSON-LD valid — 0 errors
        </div>
      </div>
}, {
  eyebrow: 'Technical SEO',
  title: 'Never lose traffic to broken links again',
  desc: 'Deleted a product? Changed a URL? SEO Suite scans your store 24/7, catches every 404, and lets you fix it with a one-click 301 redirect, so you keep your rankings and your customers.',
  bullets: ['Automatic 404 detection across your whole store', 'One-click 301 redirects to recover lost link equity', 'Bulk redirect manager with import/export'],
  mockup: <div className="space-y-3" data-id="element-1292">
        <div className="flex items-center justify-between" data-id="element-1293">
          <span className="text-sm font-semibold text-white" data-id="element-1294">
            Broken Link Manager
          </span>
          <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-full border border-emerald-400/20" data-id="element-1295">
            Auto-scanning
          </span>
        </div>
        {[{
      from: '/products/old-hoodie',
      to: '/products/new-hoodie'
    }, {
      from: '/collections/summer-22',
      to: '/collections/summer'
    }].map(r => <div key={r.from} className="rounded-lg border border-white/10 bg-white/5 p-3" data-id="element-1296">
            <div className="flex items-center gap-2 text-xs" data-id="element-1297">
              <LinkIcon className="w-3.5 h-3.5 text-emerald-400 shrink-0" data-id="element-1298" />
              <span className="text-gray-400 truncate" data-id="element-1299">{r.from}</span>
              <ArrowRightIcon className="w-3 h-3 text-gray-600 shrink-0" data-id="element-1300" />
              <span className="text-emerald-400 truncate" data-id="element-1301">{r.to}</span>
            </div>
          </div>)}
        <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-1302">
          <CheckCircle2Icon className="w-4 h-4 text-emerald-400" data-id="element-1303" /> 38 redirects
          active · 0 unresolved 404s
        </div>
      </div>
}];
const BENEFITS = [{
  icon: <MapIcon className="w-6 h-6 text-emerald-400" data-id="element-1304" />,
  title: 'Smart Sitemaps',
  desc: 'Always-fresh XML sitemaps submitted automatically for faster indexing.'
}, {
  icon: <GaugeIcon className="w-6 h-6 text-emerald-400" data-id="element-1305" />,
  title: 'Speed Insights',
  desc: 'Core Web Vitals monitoring with actionable fixes to boost your score.'
}, {
  icon: <GlobeIcon className="w-6 h-6 text-emerald-400" data-id="element-1306" />,
  title: 'Multi-Language SEO',
  desc: 'Hreflang tags handled automatically for international storefronts.'
}, {
  icon: <ShieldCheckIcon className="w-6 h-6 text-emerald-400" data-id="element-1307" />,
  title: 'No-Code Setup',
  desc: 'Installs in minutes. No developer, no theme edits, no headaches.'
}];
const STATS = [{
  value: '+145%',
  label: 'Avg. organic traffic lift'
}, {
  value: '12,450+',
  label: 'Images optimized per store'
}, {
  value: '98/100',
  label: 'Average SEO health score'
}, {
  value: '4.9★',
  label: 'Merchant rating'
}];
const TESTIMONIALS = [{
  quote: 'Our organic traffic nearly doubled in two months. The AI meta tags alone were worth it. We stopped writing them by hand entirely.',
  name: 'Sarah L.',
  role: 'Founder, Apparel Brand'
}, {
  quote: 'Rich snippets got us star ratings in Google and our click-through rate jumped. Setup took ten minutes with zero code.',
  name: 'Daniel R.',
  role: 'Ecommerce Manager'
}, {
  quote: 'The broken-link manager saved rankings we would have lost after a big catalog cleanup. Genuinely essential.',
  name: 'Priya M.',
  role: 'Shopify Plus Merchant'
}];
const FAQS = [{
  q: 'Do I need any coding or SEO experience?',
  a: 'None at all. SEO Suite is fully no-code. It installs in minutes, runs automatically in the background, and gives you plain-English recommendations.'
}, {
  q: 'Will it slow down my store?',
  a: 'The opposite. SEO Suite compresses images and improves Core Web Vitals, which typically makes your store faster, not slower.'
}, {
  q: 'Is there a free plan?',
  a: 'Yes. You can start completely free, optimize your store, and upgrade only when you need advanced automation and unlimited pages.'
}, {
  q: 'How quickly will I see results?',
  a: 'Technical fixes apply instantly. Ranking and traffic improvements typically show within a few weeks as Google re-crawls your optimized pages.'
}];
export function SEOSuitePage() {
  usePageSEO({
    canonical: '/seo-suite',
    keywords: 'Shopify SEO app, SEO Suite, Shopify SEO optimization, auto meta tags Shopify, JSON-LD Shopify, image optimizer Shopify',
    ogType: 'product',
    jsonLd: [breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Apps',
      path: '/apps'
    }, {
      name: 'SEO Suite',
      path: '/seo-suite'
    }]), {
      ...softwareAppSchema({
        name: 'SEO Suite',
        description: 'The all-in-one Shopify SEO app: AI meta tags, image optimization, JSON-LD rich snippets, broken-link fixes, and sitemaps.',
        path: '/seo-suite',
        category: 'BusinessApplication'
      }),
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '128'
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      }
    }, faqSchema(FAQS.map(f => ({
      question: f.q,
      answer: f.a
    })))],
    title: 'SEO Suite for Shopify | Design Flow Digitals',
    description: 'SEO Suite is the all-in-one Shopify SEO app: AI meta tags, image optimization, JSON-LD rich snippets, broken-link fixes, and sitemaps. Boost organic traffic and sales. Install free.'
  });
  return <div className="w-full" data-id="element-1308">
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center pt-32 pb-20 overflow-hidden" data-id="element-1309">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/20 via-black to-black pointer-events-none" data-id="element-1310" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-1311" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1312">
          <div data-id="element-1313">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-emerald-500/30 bg-emerald-500/10" data-id="element-1314">
              <SparklesIcon className="w-4 h-4 text-emerald-400" data-id="element-1315" />
              <span className="text-sm font-medium text-emerald-300" data-id="element-1316">
                Latest Release · Shopify App
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" data-id="element-1317">
              Outrank your competition on Google
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 mb-8 leading-relaxed" data-id="element-1318">
              SEO Suite is the all-in-one Shopify SEO app that automates meta
              tags, image optimization, rich snippets, and technical fixes, so
              you get more organic traffic and more sales, on autopilot.
            </p>
            <div className="flex items-center gap-4 mb-10" data-id="element-1319">
              <StarRow data-id="element-1320" />
              <span className="text-sm text-gray-400" data-id="element-1321">
                4.9 rating · Free plan available
              </span>
            </div>
            <div className="flex flex-wrap gap-4" data-id="element-1322">
              <InstallButton size="lg" data-id="element-1323" />
              <a href="#features" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-1324">
                See how it works
              </a>
            </div>
          </div>

          {/* Hero Mockup */}
          <div className="relative" data-id="element-1325">
            <div className="absolute inset-0 bg-emerald-500/20 blur-[100px] rounded-full" data-id="element-1326" />
            <div className="relative z-10" data-id="element-1327">
              <AppFrame data-id="element-1328">
                <div className="flex justify-between items-center mb-6" data-id="element-1329">
                  <h3 className="text-lg font-bold text-white" data-id="element-1330">
                    SEO Health Score
                  </h3>
                  <span className="text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full text-sm font-bold border border-emerald-400/20" data-id="element-1331">
                    98/100
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-1332">
                  <div className="bg-white/5 p-4 rounded-lg border border-white/5" data-id="element-1333">
                    <p className="text-xs text-gray-400 mb-1" data-id="element-1334">
                      Organic Traffic
                    </p>
                    <p className="text-xl font-bold text-white flex items-center gap-1.5" data-id="element-1335">
                      +145%{' '}
                      <TrendingUpIcon className="w-4 h-4 text-emerald-400" data-id="element-1336" />
                    </p>
                  </div>
                  <div className="bg-white/5 p-4 rounded-lg border border-white/5" data-id="element-1337">
                    <p className="text-xs text-gray-400 mb-1" data-id="element-1338">
                      Images Optimized
                    </p>
                    <p className="text-xl font-bold text-white" data-id="element-1339">12,450</p>
                  </div>
                </div>
                <div className="space-y-2" data-id="element-1340">
                  {['Meta Tags Optimized', 'Broken Links Fixed', 'Schema Added'].map(item => <div key={item} className="flex items-center justify-between p-2.5 bg-emerald-500/10 rounded-lg border border-emerald-500/20" data-id="element-1341">
                      <div className="flex items-center gap-2.5" data-id="element-1342">
                        <CheckCircle2Icon className="w-4 h-4 text-emerald-400" data-id="element-1343" />
                        <span className="text-sm text-emerald-100" data-id="element-1344">{item}</span>
                      </div>
                      <span className="text-xs text-emerald-400" data-id="element-1345">
                        Automated
                      </span>
                    </div>)}
                </div>
              </AppFrame>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="border-y border-white/5 bg-[#050505]" data-id="element-1346">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-1347">
          {STATS.map(s => <div key={s.label} className="text-center" data-id="element-1348">
              <p className="text-3xl md:text-4xl font-bold text-white mb-1" data-id="element-1349">
                {s.value}
              </p>
              <p className="text-sm text-gray-500" data-id="element-1350">{s.label}</p>
            </div>)}
        </div>
      </section>

      {/* Outcomes */}
      <section className="section-padding bg-black" data-id="element-1351">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1352">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1353">
            Built to grow your store, not just your checklist
          </h2>
          <p className="text-xl text-gray-400" data-id="element-1354">
            Every feature ladders up to the only three things that matter for
            your business.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1355">
          {[{
          icon: <TrendingUpIcon className="w-7 h-7 text-emerald-400" data-id="element-1356" />,
          title: 'Rank higher',
          desc: 'Clean technical SEO and optimized content push your products up the search results where buyers actually look.'
        }, {
          icon: <SearchIcon className="w-7 h-7 text-emerald-400" data-id="element-1357" />,
          title: 'Get found',
          desc: 'Rich snippets, image SEO, and fast indexing put your store in front of high-intent shoppers across Google.'
        }, {
          icon: <RocketIcon className="w-7 h-7 text-emerald-400" data-id="element-1358" />,
          title: 'Sell more',
          desc: 'More qualified organic traffic means more sales, without paying for every single click.'
        }].map(o => <div key={o.title} className="glass-card p-8 border-white/10 hover:border-emerald-500/30 transition-colors" data-id="element-1359">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-6 border border-emerald-500/20" data-id="element-1360">
                {o.icon}
              </div>
              <h3 className="text-2xl font-bold text-white mb-3" data-id="element-1361">{o.title}</h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-1362">{o.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Detailed Feature Blocks with mockups */}
      <section id="features" className="bg-[#020202] border-y border-white/5" data-id="element-1363">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 space-y-28" data-id="element-1364">
          {FEATURE_BLOCKS.map((f, i) => <div key={f.title} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-1365">
              <div className={i % 2 === 1 ? 'lg:order-2' : ''} data-id="element-1366">
                <p className="text-sm font-bold text-emerald-400 uppercase tracking-widest mb-4" data-id="element-1367">
                  {f.eyebrow}
                </p>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-5 leading-tight" data-id="element-1368">
                  {f.title}
                </h3>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-1369">
                  {f.desc}
                </p>
                <ul className="space-y-4" data-id="element-1370">
                  {f.bullets.map(b => <li key={b} className="flex items-start gap-3 text-gray-300" data-id="element-1371">
                      <CheckCircle2Icon className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" data-id="element-1372" />
                      <span data-id="element-1373">{b}</span>
                    </li>)}
                </ul>
              </div>
              <div className={i % 2 === 1 ? 'lg:order-1' : ''} data-id="element-1374">
                {f.mockup && <AppFrame data-id="element-1375">{f.mockup}</AppFrame>}
              </div>
            </div>)}
        </div>
      </section>

      {/* Benefits grid */}
      <section className="section-padding bg-black" data-id="element-1376">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1377">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1378">Everything else, handled</h2>
          <p className="text-xl text-gray-400" data-id="element-1379">
            SEO Suite covers the full technical stack so nothing slips through
            the cracks.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-id="element-1380">
          {BENEFITS.map(b => <div key={b.title} className="glass-card p-6 border-white/10 hover:border-emerald-500/30 transition-colors" data-id="element-1381">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-5 border border-emerald-500/20" data-id="element-1382">
                {b.icon}
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1383">{b.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed" data-id="element-1384">{b.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-[#050505] border-y border-white/5" data-id="element-1385">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1386">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1387">
            Merchants love the results
          </h2>
          <p className="text-xl text-gray-400" data-id="element-1388">
            Join thousands of Shopify stores growing their organic traffic with
            SEO Suite.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1389">
          {TESTIMONIALS.map(t => <div key={t.name} className="glass-card p-8 border-white/10" data-id="element-1390">
              <StarRow data-id="element-1391" />
              <p className="text-gray-300 leading-relaxed my-6" data-id="element-1392">“{t.quote}”</p>
              <div data-id="element-1393">
                <p className="font-bold text-white" data-id="element-1394">{t.name}</p>
                <p className="text-sm text-gray-500" data-id="element-1395">{t.role}</p>
              </div>
            </div>)}
        </div>
      </section>

      {/* Pricing */}
      <section className="section-padding bg-black" data-id="element-1396">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1397">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1398">
            Start free. Upgrade when you grow.
          </h2>
          <p className="text-xl text-gray-400" data-id="element-1399">
            No risk, no code, no contracts. Install and start optimizing today.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-1400">
          <div className="glass-card p-10 border-white/10 flex flex-col" data-id="element-1401">
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1402">Free</h3>
            <p className="text-gray-400 mb-6" data-id="element-1403">Perfect to get started</p>
            <div className="mb-8" data-id="element-1404">
              <span className="text-5xl font-bold text-white" data-id="element-1405">$0</span>
              <span className="text-gray-400" data-id="element-1406">/mo</span>
            </div>
            <ul className="space-y-4 mb-8 flex-1" data-id="element-1407">
              {['AI meta tags (limited pages)', 'Image compression', 'Basic SEO health score', 'Broken link detection'].map(f => <li key={f} className="flex items-center gap-3 text-gray-300" data-id="element-1408">
                  <CheckCircle2Icon className="w-5 h-5 text-emerald-400" data-id="element-1409" /> {f}
                </li>)}
            </ul>
            <a href={SHOPIFY_URL} target="_blank" rel="noopener noreferrer" className="w-full text-center py-4 rounded-full font-bold text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-1410">
              Install Free
            </a>
          </div>

          <div className="glass-card p-10 border-emerald-500/30 bg-emerald-500/5 relative overflow-hidden flex flex-col" data-id="element-1411">
            <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-1412">
              MOST POPULAR
            </div>
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1413">Pro</h3>
            <p className="text-gray-400 mb-6" data-id="element-1414">
              For stores serious about growth
            </p>
            <div className="mb-8" data-id="element-1415">
              <span className="text-5xl font-bold text-white" data-id="element-1416">$19</span>
              <span className="text-gray-400" data-id="element-1417">/mo</span>
            </div>
            <ul className="space-y-4 mb-8 flex-1" data-id="element-1418">
              {['Unlimited AI meta tags', 'Bulk image optimization & ALT tags', 'JSON-LD rich snippets', 'Automatic 301 redirect manager', 'Sitemaps & priority indexing'].map(f => <li key={f} className="flex items-center gap-3 text-gray-300" data-id="element-1419">
                  <CheckCircle2Icon className="w-5 h-5 text-emerald-400" data-id="element-1420" /> {f}
                </li>)}
            </ul>
            <a href={SHOPIFY_URL} target="_blank" rel="noopener noreferrer" className="w-full text-center py-4 rounded-full font-bold bg-emerald-500 text-white hover:bg-emerald-400 transition-colors shadow-[0_0_20px_rgba(16,185,129,0.3)]" data-id="element-1421">
              Start Pro
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-[#020202] border-t border-white/5" data-id="element-1422">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1423">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1424">
            Frequently asked questions
          </h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-5" data-id="element-1425">
          {FAQS.map(f => <div key={f.q} className="glass-card p-6 border-white/10" data-id="element-1426">
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1427">{f.q}</h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-1428">{f.a}</p>
            </div>)}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 px-6 text-center bg-gradient-to-b from-transparent to-emerald-900/20" data-id="element-1429">
        <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1430">
          Ready to dominate search results?
        </h2>
        <p className="text-xl text-gray-400 mb-4 max-w-2xl mx-auto" data-id="element-1431">
          Install SEO Suite today and start turning Google into your most
          profitable sales channel.
        </p>
        <div className="flex items-center justify-center gap-3 mb-10" data-id="element-1432">
          <StarRow data-id="element-1433" />
          <span className="text-sm text-gray-400" data-id="element-1434">
            4.9 rating · Free plan · Installs in minutes
          </span>
        </div>
        <InstallButton size="lg" data-id="element-1435" />
      </section>
    </div>;
}